import { useState } from 'react';
import {
  useOrganisations,
  useOrganisation,
  useInitiateTakeover,
  useOrganisationTrend,
  useRunUsageSnapshots,
  downloadOrganisationsCsv,
  type AccountRow,
  type UsageSnapshotRow,
} from '../../../api/super-admin.api';
import { useAuthStore } from '../../../stores/auth.store';
import { openImpersonationTab } from '../../../lib/impersonation-handoff';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

export function OrganisationsPage() {
  const { data: orgs, isLoading } = useOrganisations();
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: 'var(--space-4)', alignItems: 'flex-start' }}>
      <section style={cardStyle}>
        <header style={{ ...cardHeaderStyle, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
          <div>
            <h2 style={cardTitleStyle}>Organisations</h2>
            <p style={cardSubtitleStyle}>{orgs?.length ?? 0} total · derived from users_cache</p>
          </div>
          <OrgToolbar />
        </header>

        {isLoading && <div style={{ padding: 'var(--space-4)' }}>Loading…</div>}
        {!isLoading && (orgs?.length ?? 0) === 0 && <div style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>No organisations have launched Engage yet.</div>}

        {(orgs?.length ?? 0) > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <Th>Organisation</Th>
                  <Th align="right">Users</Th>
                  <Th align="right">Campaigns</Th>
                  <Th align="right">Mailboxes</Th>
                  <Th align="right">Sends today</Th>
                  <Th>Last active</Th>
                </tr>
              </thead>
              <tbody>
                {orgs!.map((o) => (
                  <tr key={o.org_id}
                    onClick={() => setSelectedOrgId(o.org_id)}
                    style={{ cursor: 'pointer', backgroundColor: selectedOrgId === o.org_id ? 'var(--color-sidebar-active-bg)' : undefined }}
                  >
                    <Td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
                          {o.company_name || 'Unnamed organisation'}
                        </span>
                        <code style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{o.org_id}</code>
                      </div>
                    </Td>
                    <Td align="right">{o.user_count}</Td>
                    <Td align="right">{o.active_campaigns} / {o.total_campaigns}</Td>
                    <Td align="right">{o.active_mailboxes} / {o.connected_mailboxes}</Td>
                    <Td align="right">{o.sends_today}</Td>
                    <Td>{o.last_active_at ? new Date(o.last_active_at).toLocaleDateString() : '-'}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <OrganisationDetailPanel orgId={selectedOrgId} />
    </div>
  );
}

function OrganisationDetailPanel({ orgId }: { orgId: string | null }) {
  const { data, isLoading } = useOrganisation(orgId);
  const takeover = useInitiateTakeover();
  const currentEmail = useAuthStore((s) => s.user?.email);

  if (!orgId) {
    return (
      <aside style={cardStyle}>
        <header style={cardHeaderStyle}>
          <h2 style={cardTitleStyle}>Detail</h2>
          <p style={cardSubtitleStyle}>Pick an organisation to drill in.</p>
        </header>
      </aside>
    );
  }

  if (isLoading || !data) {
    return (
      <aside style={cardStyle}>
        <header style={cardHeaderStyle}>
          <h2 style={cardTitleStyle}>Loading…</h2>
        </header>
      </aside>
    );
  }

  return (
    <aside style={cardStyle}>
      <header style={cardHeaderStyle}>
        <h2 style={cardTitleStyle}>{data.company_name || 'Unnamed organisation'}</h2>
        <p style={cardSubtitleStyle}>
          <code style={{ fontSize: 'var(--text-xs)' }}>{data.org_id}</code> · {data.user_count} users · {data.active_campaigns} active campaigns · {data.sends_today} sends today
        </p>
      </header>

      <div style={{ padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <TrendPanel orgId={data.org_id} />

        <SectionLabel>Accounts</SectionLabel>
        {data.accounts.map((a) => (
          <AccountRowView
            key={a.jt_user_id}
            account={a}
            isSelf={a.email.toLowerCase() === (currentEmail ?? '').toLowerCase()}
            onActAs={() => triggerTakeover(a, takeover)}
            pending={takeover.isPending}
          />
        ))}
      </div>
    </aside>
  );
}

function AccountRowView({
  account, isSelf, onActAs, pending,
}: {
  account: AccountRow;
  isSelf: boolean;
  onActAs: () => void;
  pending: boolean;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-input)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)' }}>{account.name || '(no name)'}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{account.email}</span>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Role: {account.role}</span>
      </div>
      <button
        type="button"
        onClick={onActAs}
        disabled={isSelf || pending}
        title={isSelf ? 'Cannot act as yourself' : 'Open a new tab signed in as this user'}
        style={{
          padding: 'var(--space-1) var(--space-3)',
          borderRadius: 'var(--btn-radius)',
          border: '1px solid var(--color-warning)',
          backgroundColor: 'transparent',
          color: 'var(--color-warning)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          cursor: isSelf || pending ? 'not-allowed' : 'pointer',
          opacity: isSelf || pending ? 0.5 : 1,
          whiteSpace: 'nowrap',
        }}
      >
        Act as
      </button>
    </div>
  );
}

/**
 * Section 13B.2 handoff — opens the takeover session in a new tab via the
 * shared localStorage handoff in lib/impersonation-handoff.ts.
 */
async function triggerTakeover(
  account: AccountRow,
  takeover: ReturnType<typeof useInitiateTakeover>
) {
  try {
    const result = await takeover.mutateAsync(account.jt_user_id);
    openImpersonationTab({
      token: result.token,
      user: {
        org_id: result.target.org_id,
        account_id: result.target.jt_user_id,
        user_id: result.target.jt_user_id,
        role: result.target.role,
        name: result.target.name,
        email: result.target.email,
      },
      actorEmail: useAuthStore.getState().user?.email ?? null,
      kind: 'takeover',
    });
  } catch (err) {
    const message = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
      ?? (err as Error).message
      ?? 'Takeover failed';
    window.alert(message);
  }
}

/* ── Toolbar + trend ────────────────────────────────────────────────── */

function OrgToolbar() {
  const runSnapshots = useRunUsageSnapshots();
  const token = useAuthStore((s) => s.token);
  const [csvBusy, setCsvBusy] = useState(false);

  async function exportCsv() {
    setCsvBusy(true);
    try {
      await downloadOrganisationsCsv(token);
    } catch (err) {
      window.alert((err as Error).message);
    } finally {
      setCsvBusy(false);
    }
  }

  async function snapshotNow() {
    // Run synchronously in dev so the user sees the chart populate
    // immediately. In a many-org production setup operators should call
    // sync=false from a script — the inline path is intentionally bounded
    // and small. The 202 response from the async path doesn't include
    // counts, so we still log it.
    try {
      const result = await runSnapshots.mutateAsync({ sync: true });
      const summary = result.mode === 'sync'
        ? `Snapshot complete: ${result.orgs ?? 0} orgs, ${(result.failed ?? []).length} failed.`
        : 'Snapshot enqueued.';
      window.alert(summary);
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } }; message?: string })
        ?.response?.data?.error ?? (err as Error).message ?? 'Snapshot failed';
      window.alert(message);
    }
  }

  return (
    <div style={{ display: 'flex', gap: 'var(--space-2)', flexShrink: 0 }}>
      <ToolbarBtn onClick={exportCsv} disabled={csvBusy}>
        {csvBusy ? 'Exporting…' : 'Export CSV'}
      </ToolbarBtn>
      <ToolbarBtn onClick={snapshotNow} disabled={runSnapshots.isPending}>
        {runSnapshots.isPending ? 'Snapshotting…' : 'Snapshot now'}
      </ToolbarBtn>
    </div>
  );
}

function ToolbarBtn({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: 'var(--space-1) var(--space-3)',
        borderRadius: 'var(--btn-radius)',
        border: '1px solid var(--color-border-default)',
        backgroundColor: 'transparent',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function TrendPanel({ orgId }: { orgId: string }) {
  const { data: rows, isLoading } = useOrganisationTrend(orgId, 30);
  const [metric, setMetric] = useState<keyof Omit<UsageSnapshotRow, 'snapshot_date'>>('total_sends');

  const series = (rows ?? []).map((r) => ({
    date: r.snapshot_date.slice(0, 10),
    value: r[metric],
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
        <SectionLabel>30-day trend</SectionLabel>
        <select
          value={metric}
          onChange={(e) => setMetric(e.target.value as typeof metric)}
          style={{
            height: 'var(--btn-height-sm)',
            padding: '0 var(--space-2)',
            borderRadius: 'var(--btn-radius)',
            border: '1px solid var(--color-border-default)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-xs)',
          }}
        >
          <option value="total_sends">Sends</option>
          <option value="active_campaigns">Active campaigns</option>
          <option value="marketing_contacts_count">Marketing contacts</option>
          <option value="connected_mailboxes">Mailboxes</option>
          <option value="active_users">Active users</option>
        </select>
      </div>

      {isLoading && <div style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Loading…</div>}

      {!isLoading && series.length === 0 && (
        <div style={{ padding: 'var(--space-3)', border: '1px dashed var(--color-border-strong)', borderRadius: 'var(--radius-input)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', textAlign: 'center' }}>
          No snapshots yet. Click <strong>Snapshot now</strong> to seed the first row, or wait for the nightly cron at 00:05 UTC.
        </div>
      )}

      {series.length > 0 && (
        <div style={{ width: '100%', height: 140 }}>
          <ResponsiveContainer>
            <LineChart data={series} margin={{ top: 6, right: 8, bottom: 0, left: -16 }}>
              <CartesianGrid stroke="var(--color-border-default)" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
                tickFormatter={(d: string) => d.slice(5)}
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 12,
                  borderRadius: 6,
                  border: '1px solid var(--color-border-default)',
                }}
              />
              <Line type="monotone" dataKey="value" stroke="var(--color-brand-blue)" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

/* ── Visual primitives — shared between restricted pages ── */

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
  borderRadius: 'var(--card-radius)',
  boxShadow: 'var(--card-shadow)',
  overflow: 'hidden',
};

const cardHeaderStyle: React.CSSProperties = {
  padding: 'var(--space-4) var(--space-5)',
  borderBottom: '1px solid var(--color-border-default)',
};

const cardTitleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-xl)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-text-brand)',
  margin: 0,
};

const cardSubtitleStyle: React.CSSProperties = {
  marginTop: '2px',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-text-muted)',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-sm)',
};

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      style={{
        textAlign: align,
        padding: 'var(--space-2) var(--space-4)',
        fontSize: 'var(--text-xs)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--tracking-caps)',
        color: 'var(--color-text-muted)',
        borderBottom: '1px solid var(--color-border-default)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <td style={{ textAlign: align, padding: 'var(--space-2) var(--space-4)', verticalAlign: 'middle', borderBottom: '1px solid var(--color-border-subtle, var(--color-border-default))' }}>
      {children}
    </td>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)' }}>
      {children}
    </span>
  );
}
